// COLLECTION GALLERY SLIDER

(() => {
  if (window.__finkidCollectionGallerySliderInit) return;
  window.__finkidCollectionGallerySliderInit = true;

  const VISIBILITY_THRESHOLD = 0.7;
  const DRAG_THRESHOLD = 6;

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  function maxScrollLeft(track) {
    return Math.max(0, track.scrollWidth - track.clientWidth);
  }

  function updateArrows(track, prevBtn, nextBtn) {
    const max = maxScrollLeft(track);
    const left = track.scrollLeft;

    prevBtn.disabled = left <= 2;
    nextBtn.disabled = left >= max - 2;
  }

  function closestIndex(track, slides) {
    const trackRect = track.getBoundingClientRect();
    const centerX = trackRect.left + trackRect.width / 2;

    let best = 0;
    let bestDist = Infinity;

    for (let i = 0; i < slides.length; i++) {
      const rect = slides[i].getBoundingClientRect();
      const slideCenter = rect.left + rect.width / 2;
      const dist = Math.abs(slideCenter - centerX);

      if (dist < bestDist) {
        best = i;
        bestDist = dist;
      }
    }

    return best;
  }

  function snapToSlide(track, slide) {
    if (!slide) return;

    slide.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }

  function setupVisibilityPaging(track, slides) {
    if (!('IntersectionObserver' in window)) {
      return {
        destroy() {},
        getStep() {
          return 1;
        }
      };
    }

    const visible = new Set();

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const idx = slides.indexOf(entry.target);
          if (idx === -1) continue;

          if (entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
            visible.add(idx);
          } else {
            visible.delete(idx);
          }
        }
      },
      {
        root: track,
        threshold: VISIBILITY_THRESHOLD,
        rootMargin: '1px'
      }
    );

    slides.forEach((slide) => io.observe(slide));

    return {
      destroy() {
        io.disconnect();
      },
      getStep() {
        return Math.max(1, visible.size || 1);
      }
    };
  }

  function enableMouseDrag(track, slides) {
    let pointerId = null;
    let startX = 0;
    let startLeft = 0;
    let dragging = false;
    let suppressClick = false;

    track.addEventListener('dragstart', (e) => e.preventDefault());

    track.addEventListener(
      'click',
      (e) => {
        if (!suppressClick) return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        suppressClick = false;
      },
      true
    );

    function resetDragState() {
      dragging = false;
      track.style.scrollSnapType = '';
      track.style.scrollBehavior = '';
      track.style.cursor = '';
      document.documentElement.style.userSelect = '';
    }

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      if (e.button !== 0) return;

      pointerId = e.pointerId;
      startX = e.clientX;
      startLeft = track.scrollLeft;
      dragging = false;
      suppressClick = false;
    });

    track.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      if (pointerId == null || e.pointerId !== pointerId) return;

      const delta = startX - e.clientX;

      if (!dragging && Math.abs(delta) < DRAG_THRESHOLD) return;

      if (!dragging) {
        dragging = true;
        suppressClick = true;

        try {
          track.setPointerCapture(e.pointerId);
        } catch (_) {}

        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
        track.style.cursor = 'grabbing';
        document.documentElement.style.userSelect = 'none';
      }

      e.preventDefault();
      track.scrollLeft = clamp(startLeft + delta, 0, maxScrollLeft(track));
    });

    const handlePointerEnd = (e) => {
      if (pointerId == null || e.pointerId !== pointerId) return;

      pointerId = null;

      if (!dragging) return;

      try {
        track.releasePointerCapture(e.pointerId);
      } catch (_) {}

      resetDragState();
      snapToSlide(track, slides[closestIndex(track, slides)]);

      window.setTimeout(() => {
        suppressClick = false;
      }, 0);
    };

    track.addEventListener('pointerup', handlePointerEnd);
    track.addEventListener('pointercancel', handlePointerEnd);
    track.addEventListener('pointercapturelost', handlePointerEnd);
  }

  function initCarousel(carousel) {
    if (!carousel || carousel.dataset.fcgInit === 'true') return;
    carousel.dataset.fcgInit = 'true';

    const track = carousel.querySelector('[data-fcg-track]');
    const prevBtn = carousel.querySelector('[data-fcg-prev]');
    const nextBtn = carousel.querySelector('[data-fcg-next]');

    if (!track || !prevBtn || !nextBtn) return;

    const slides = Array.from(track.querySelectorAll('[data-fcg-slide]'));

    if (slides.length <= 1) {
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      return;
    }

    const visibilityPaging = setupVisibilityPaging(track, slides);

    prevBtn.addEventListener('click', () => {
      const current = closestIndex(track, slides);
      const step = visibilityPaging.getStep();
      const nextIndex = clamp(current - step, 0, slides.length - 1);
      snapToSlide(track, slides[nextIndex]);
    });

    nextBtn.addEventListener('click', () => {
      const current = closestIndex(track, slides);
      const step = visibilityPaging.getStep();
      const nextIndex = clamp(current + step, 0, slides.length - 1);
      snapToSlide(track, slides[nextIndex]);
    });

    let raf = 0;
    track.addEventListener(
      'scroll',
      () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          updateArrows(track, prevBtn, nextBtn);
        });
      },
      { passive: true }
    );

    window.addEventListener('resize', () => {
      updateArrows(track, prevBtn, nextBtn);
    });

    enableMouseDrag(track, slides);
    updateArrows(track, prevBtn, nextBtn);

    carousel.__fcgDestroy = () => {
      visibilityPaging.destroy();
    };
  }

  function initAll(root = document) {
    const carousels = root.matches?.('[data-fcg-carousel]')
      ? [root]
      : root.querySelectorAll('[data-fcg-carousel]');

    carousels.forEach(initCarousel);
  }

  document.addEventListener('DOMContentLoaded', () => initAll(document));
  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target || document);
  });
})();
